import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "./Layout";
import { normalizeDigits, normalizePhone } from "../utils/normalizeDigits";
import { FiArrowRight, FiCheckCircle, FiEye, FiEyeOff, FiKey, FiLock, FiMoon, FiSun } from "react-icons/fi";
import useAuthTheme from "./useAuthTheme";
import "./auth.css";
import "../pages/dashboard.css";

const API_BASE = (process.env.REACT_APP_API_URL || "/api/v1").replace(/\/$/, "");

export default function ResetPassword() {
  const loc = useLocation();
  const navigate = useNavigate();

  // شماره را از مرحله قبل گرفته و نرمال می‌کنیم
  const phone = normalizePhone(loc.state?.phone || "");

  // فقط در محیط توسعه برای تست
  const devOtp = normalizeDigits(loc.state?.otp || "");

  const [otp, setOtp] = useState(devOtp || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [theme, setTheme] = useAuthTheme();

  if (!phone) {
    return (
      <Layout className="login-auth-wrapper recovery-auth-wrapper" cardClassName="login-card register-card recovery-card">
        <div className="register-toolbar">
          <button type="button" className="register-icon-button" onClick={() => navigate("/forgot-password")} aria-label="بازگشت"><FiArrowRight /></button>
          <button type="button" className="register-icon-button" onClick={() => setTheme((value) => value === "dark" ? "light" : "dark")} aria-label="تغییر پوسته">{theme === "dark" ? <FiSun /> : <FiMoon />}</button>
        </div>
        <div className="recovery-heading"><span><FiKey /></span><div><h1>اطلاعات بازیابی یافت نشد</h1><p>برای دریافت کد تأیید، دوباره شماره تلفن حساب را وارد کنید.</p></div></div>
        <button className="recovery-submit" type="button" onClick={() => navigate("/forgot-password")}><FiArrowRight /> بازگشت به بازیابی رمز</button>
      </Layout>
    );
  }

  const validate = () => {
    const finalOtp = normalizeDigits(otp);

    if (!finalOtp || finalOtp.length !== 6) {
      return "کد تأیید باید ۶ رقمی باشد.";
    }

    if (newPassword.length < 8) {
      return "رمز عبور باید حداقل ۸ کاراکتر باشد.";
    }

    if (!/[^\d\s]/.test(newPassword) || !/\d/.test(newPassword)) {
      return "رمز عبور باید حداقل شامل یک حرف و یک عدد باشد.";
    }

    if (newPassword !== confirmPassword) {
      return "رمز عبور و تأیید آن یکسان نیستند.";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      setMessage(err);
      setIsError(true);
      return;
    }

    const finalOtp = normalizeDigits(otp);

    setLoading(true);
    setMessage("در حال بررسی...");
    setIsError(false);

    try {
      const resetRes = await fetch(`${API_BASE}/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone,
          otp: finalOtp,
          new_password: newPassword,
        }),
      });

      const resetData = await resetRes.json().catch(() => ({}));

      if (!resetRes.ok) {
        setMessage(
          resetData.detail ||
            resetData.message ||
            "خطا در تغییر رمز عبور."
        );
        setIsError(true);
        setLoading(false);
        return;
      }

      setMessage("رمز عبور با موفقیت تغییر کرد.");
      setIsError(false);
      setLoading(false);

      setTimeout(() => navigate("/auth/login"), 1200);
    } catch (err) {
      console.error("ResetPassword error:", err);
      setMessage("خطا در اتصال به سرور");
      setIsError(true);
      setLoading(false);
    }
  };

  return (
    <Layout className="login-auth-wrapper recovery-auth-wrapper" cardClassName="login-card register-card recovery-card">
      <div className="register-toolbar">
        <button type="button" className="register-icon-button" onClick={() => navigate("/forgot-password")} aria-label="بازگشت"><FiArrowRight /></button>
        <button type="button" className="register-icon-button" onClick={() => setTheme((value) => value === "dark" ? "light" : "dark")} aria-label="تغییر پوسته">{theme === "dark" ? <FiSun /> : <FiMoon />}</button>
      </div>
      <div className="recovery-heading"><span><FiLock /></span><div><h1>تنظیم رمز عبور جدید</h1><p>کد ارسال‌شده به شماره <bdi>{phone}</bdi> را همراه رمز جدید وارد کنید.</p></div></div>
      <div className="recovery-steps"><span className="is-complete"><b><FiCheckCircle /></b> شماره تماس</span><i /><span className="is-active"><b>۲</b> رمز جدید</span></div>

      <form className="recovery-form" onSubmit={handleSubmit} noValidate>
        <label htmlFor="reset-otp">کد تأیید ۶ رقمی</label>
        <div className="register-input-icon"><FiKey /><input
          id="reset-otp"
          type="text"
          inputMode="numeric"
          placeholder="ــــــ"
          value={otp}
          onChange={(e) => setOtp(normalizeDigits(e.target.value).slice(0, 6))}
          maxLength={6}
        /></div>

        <label htmlFor="reset-password">رمز عبور جدید</label>
        <div className="register-password"><input
          id="reset-password"
          type={showPassword ? "text" : "password"}
          placeholder="حداقل ۸ کاراکتر، شامل حرف و عدد"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
        /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label="نمایش یا پنهان کردن رمز">{showPassword ? <FiEyeOff /> : <FiEye />}</button></div>

        <label htmlFor="reset-confirm">تکرار رمز عبور جدید</label>
        <input
          id="reset-confirm"
          type={showPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
        />

        {message && <div className={`login-message ${isError ? "is-error" : "is-info"}`} role={isError ? "alert" : "status"}>{message}</div>}
        <button className="recovery-submit" type="submit" disabled={loading}>
          {loading ? <><span className="login-spinner" /> در حال بررسی...</> : <><FiCheckCircle /> ثبت رمز جدید</>}
        </button>
      </form>
    </Layout>
  );
}
